import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaEye,
  FaBalanceScale,
  FaPlus,
  FaCheckCircle,
  FaClock,
  FaArrowDown,
  FaArrowUp
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight, FiRefreshCw } from "react-icons/fi";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

const RebalancePage = () => {
  const [data, setData] = useState([]);
  const [filteredType, setFilteredType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/rebalance?page=${page}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await res.json();
      setData(result.data || []);
      setMeta({
        current_page: result.meta?.current_page || 1,
        last_page: result.meta?.last_page || 1,
        total: result.meta?.total || 0,
      });
    } catch (err) {
      console.error("Fetch error:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleApprove = async (id) => {
    const confirm = await Swal.fire({
      title: "Konfirmasi Approval",
      text: "Apakah Anda yakin ingin menyetujui mutasi rebalance stok ini?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Setujui",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/rebalance/approve/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Berhasil Disetujui",
          text: "Rebalance stok berhasil disetujui.",
          timer: 1500,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });
        fetchData();
      } else {
        Swal.fire({
          icon: "error",
          title: "Gagal Approve",
          text: "Terjadi kesalahan saat menyetujui rebalance.",
          customClass: { popup: "rounded-3xl" },
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Terjadi kesalahan koneksi.",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const filteredData = data
    .filter((i) => (filteredType === "all" ? true : i.type === filteredType))
    .filter((i) => (i.objective || "").toLowerCase().includes(searchTerm.toLowerCase()));

  const totalIn = data.filter((i) => i.type === "in").length;
  const totalOut = data.filter((i) => i.type === "out").length;

  const canPrevious = page > 1;
  const canNext = page < meta.last_page;

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-100 text-emerald-600">
            <FaBalanceScale className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Rebalance Stok Obat
            </h1>
            <p className="text-xs text-gray-500">
              Monitoring dan approval perpindahan / mutasi stok obat antar outlet cabang
            </p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/rebalance/create")}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
        >
          <FaPlus className="w-3.5 h-3.5" />
          <span>Buat Rebalance Baru</span>
        </motion.button>
      </motion.div>

      {/* 2. Metric Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Rebalance (Halaman Ini)
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 font-mono">{data.length}</h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FaBalanceScale className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Stok Masuk (Type IN)
            </p>
            <h2 className="text-3xl font-extrabold text-emerald-600 font-mono">{totalIn}</h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FaArrowDown className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Stok Keluar (Type OUT)
            </p>
            <h2 className="text-3xl font-extrabold text-amber-600 font-mono">{totalOut}</h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FaArrowUp className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 3. Control & Table Panel */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {["all", "in", "out"].map((t) => (
              <button
                key={t}
                onClick={() => setFilteredType(t)}
                className={`px-4 py-2 rounded-2xl text-xs font-extrabold transition-all uppercase ${
                  filteredType === t
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {t === "all" ? "Semua" : t === "in" ? "Masuk (IN)" : "Keluar (OUT)"}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Cari berdasarkan objective..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5">Tanggal Buat</th>
                  <th className="p-3.5 text-center">Jumlah Stok</th>
                  <th className="p-3.5 text-center">Tipe Mutasi</th>
                  <th className="p-3.5">Tujuan / Objective</th>
                  <th className="p-3.5 text-center">Status Approval</th>
                  <th className="p-3.5 text-center">Aksi Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-12 text-center text-gray-400 font-semibold">
                      Belum ada data rebalance ditemukan
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 font-mono font-semibold text-gray-700">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="p-3.5 text-center font-mono font-extrabold text-gray-900 text-sm">
                        {item.stock}
                      </td>
                      <td className="p-3.5 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase border ${
                            item.type === "in"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                        >
                          {item.type}
                        </span>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-800 uppercase tracking-wide">
                        {item.objective || "-"}
                      </td>
                      <td className="p-3.5 text-center">
                        {item.approved ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 font-extrabold rounded-full text-[10px] border border-emerald-200">
                            <FaCheckCircle className="w-3 h-3" /> Disetujui
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (["owner", "admin", "superadmin"].includes(role)) {
                                handleApprove(item.id);
                              } else {
                                Swal.fire({
                                  icon: "info",
                                  title: "Akses Terbatas",
                                  text: "Hanya Owner/Admin yang dapat menyetujui rebalance.",
                                  customClass: { popup: "rounded-3xl" },
                                });
                              }
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 hover:bg-amber-100 font-extrabold rounded-full text-[10px] border border-amber-200 transition-colors cursor-pointer"
                          >
                            <FaClock className="w-3 h-3" /> Setujui Sekarang
                          </button>
                        )}
                      </td>
                      <td className="p-3.5 text-center">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => navigate(`/rebalance/detail/${item.id}`)}
                          className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-bold transition-all"
                          title="Lihat Detail Rebalance"
                        >
                          <FaEye className="w-4 h-4" />
                        </motion.button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{filteredData.length}</span> data dari total{" "}
            <span className="font-bold text-gray-700">{meta.total}</span> rebalance
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => canPrevious && setPage(page - 1)}
              disabled={!canPrevious}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                !canPrevious
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {meta.current_page} dari {meta.last_page}
            </span>
            <button
              onClick={() => canNext && setPage(page + 1)}
              disabled={!canNext}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                !canNext
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RebalancePage;
